import React, { useState } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSalesAnalytics } from "../../../hooks/useAnalytics";
import { AnalyticsFilter } from "../../../types/analytics";
import PeriodSelector from "../../../components/backend/analytics/PeriodSelector";
import RevenueChart from "../../../components/backend/analytics/RevenueChart";
import PieBreakdown from "../../../components/backend/analytics/PieBreakdown";
import DeliveryStatsCard from "../../../components/backend/analytics/DeliveryStatsCard";
import KpiStatCard from "../../../components/backend/analytics/KpiStatCard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const SalesOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AnalyticsFilter>({ period: "30d" });
  const { data: response, isLoading } = useSalesAnalytics(filter);
  const sales = response?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t("admin.analytics.salesOverview")}
          </Typography>
        </Box>
        <PeriodSelector filter={filter} onChange={setFilter} />
      </Box>

      {sales && (
        <>
          {/* Summary KPI cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalRevenue")}
                data={{
                  currentValue: sales.totalRevenue,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                prefix="$"
                icon={<AttachMoneyIcon />}
                color="#10b981"
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalEarnings")}
                data={{
                  currentValue: sales.totalEarnings,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                prefix="$"
                icon={<AccountBalanceWalletIcon />}
                color="#06b6d4"
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalOrders")}
                data={{
                  currentValue: sales.totalOrders,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                icon={<ShoppingCartIcon />}
                color="#6366f1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalItemsSold")}
                data={{
                  currentValue: sales.deliveryStats.totalItemsOrdered,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                icon={<InventoryIcon />}
                color="#ec4899"
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalDiscounts")}
                data={{
                  currentValue: sales.totalDiscounts,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                prefix="$"
                icon={<LocalOfferIcon />}
                color="#f59e0b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <KpiStatCard
                title={t("admin.analytics.totalShippingCost")}
                data={{
                  currentValue: sales.totalShippingCost,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                prefix="$"
                icon={<LocalShippingIcon />}
                color="#8b5cf6"
              />
            </Grid>
          </Grid>

          {/* Revenue Timeline */}
          <Box sx={{ mb: 4 }}>
            <RevenueChart
              title={t("admin.analytics.revenueOverTime")}
              revenueData={sales.revenueTimeline}
              orderCountData={sales.orderCountTimeline}
              height={350}
              showOrderCount
            />
          </Box>

          {/* Pie Charts */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <PieBreakdown
                title={t("admin.analytics.salesByRetailer")}
                data={sales.salesByRetailer}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieBreakdown
                title={t("admin.analytics.salesByPaymentMethod")}
                data={sales.salesByPaymentMethod}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieBreakdown
                title={t("admin.analytics.salesByLocation")}
                data={sales.salesByPickupLocation}
              />
            </Grid>
          </Grid>

          {/* Delivery Stats */}
          <DeliveryStatsCard
            title={t("admin.analytics.deliveryStats")}
            data={sales.deliveryStats}
          />
        </>
      )}
    </Box>
  );
};

export default SalesOverviewPage;
